// Per-client feature flags.
//
// The client is picked at build time with VUE_APP_CLIENT (set it in .env, e.g.
// VUE_APP_CLIENT=airfrance). Anything a client profile doesn't set falls back to
// the default profile, so the default profile must list every flag.
import defaultLoginLogo from '@/assets/client_logo.png';
import airfranceLoginLogo from '@/assets/airfrance_client_logo.png';

export const CLIENT = (process.env.VUE_APP_CLIENT || 'default').toLowerCase();

const PROFILES = {
  default: {
    // Assessment tasks (products, logbooks, progress, PDF)
    ENABLE_ASSESSMENT: true,
    // Custom B1/B2 Chapter-Scope progress, counted on instructor sign off
    ENABLE_CUSTOM_PROGRESS: true,
    // Academy147 layout for the generated logbook PDF
    ENABLE_ACADEMY_147_PDF: true,
    // Skill, P1/P2 and Classroom/Aircraft fields on tasks (task pages, import, logbooks)
    ENABLE_SKILL_P1_P2: false,
    // A/C Reg column in the trainee and instructor logbooks
    SHOW_AC_REG: true,
    // Task scopes on top of the standard ones (LOC, INS, SGH, TS, FOT, MEL, R/I)
    EXTRA_TASK_SCOPES: [],
    // Pass rate field and column on products
    ENABLE_PASS_RATE: false,
    // Evaluations can't be signed until the pass rate of main tasks is met
    ENFORCE_EVALUATION_MIN_TASKS: false,
    // PDF page uploads (cover, revisions...) on products
    ENABLE_PRODUCT_DOCUMENTS: false,
    // View Revisions button on the product page
    ENABLE_PRODUCT_REVISIONS: true,
    // Logbook Rules button in the trainee and instructor logbooks
    ENABLE_LOGBOOK_RULES: false,
    // Generate Empty Logbook button on Instructor Home
    ENABLE_EMPTY_LOGBOOK: false,
    // Upcoming Sessions folder on Instructor Home (otherwise future sessions show under Ongoing)
    SHOW_UPCOMING_SESSIONS: false,
    // Instructor Home archives a session once all its logbooks are locked with a trainee-signed
    // final evaluation (otherwise a session is archived when it is locked)
    ARCHIVE_FULLY_ASSESSED_SESSIONS: true,
    // Date of Birth, Place of Birth and Category on users (forms, lists, import, reports, PDFs)
    ENABLE_USER_BIRTH_AND_CATEGORY: true,
    // Roles that can be given in the Add/Edit User modal
    USER_ROLE_OPTIONS: [
      { value: 'admin', label: 'Admin' },
      { value: 'examiner', label: 'Examiner' },
      { value: 'instructor', label: 'Instructor' },
    ],
    // Logo on the login and reset password pages
    LOGIN_LOGO: { src: defaultLoginLogo, maxWidth: '350px', maxHeight: '60px' },
    // Docsify help opened from the Help page
    HELP_DOCS_PATH: '/elogbook_type_new/',
  },
  airfrance: {
    ENABLE_ASSESSMENT: false,
    ENABLE_CUSTOM_PROGRESS: false,
    ENABLE_ACADEMY_147_PDF: false,
    ENABLE_SKILL_P1_P2: true,
    SHOW_AC_REG: false,
    EXTRA_TASK_SCOPES: ['C/L', 'GVI', 'DVI', 'HDL'],
    ENABLE_PASS_RATE: true,
    ENFORCE_EVALUATION_MIN_TASKS: true,
    ENABLE_PRODUCT_DOCUMENTS: true,
    ENABLE_PRODUCT_REVISIONS: true,
    ENABLE_LOGBOOK_RULES: true,
    ENABLE_EMPTY_LOGBOOK: true,
    SHOW_UPCOMING_SESSIONS: false,
    ARCHIVE_FULLY_ASSESSED_SESSIONS: true,
    ENABLE_USER_BIRTH_AND_CATEGORY: false,
    USER_ROLE_OPTIONS: [
      { value: 'admin', label: 'Admin' },
      { value: 'practical_manager', label: 'Practical Element Manager' },
      { value: 'project_manager', label: 'Session Manager' },
      { value: 'instructor', label: 'Instructor' },
    ],
    LOGIN_LOGO: { src: airfranceLoginLogo, maxWidth: '300px', maxHeight: '40px' },
    HELP_DOCS_PATH: '/docsify_elogbook_type_airfrance/',
  },
};

if (!PROFILES[CLIENT]) {
  console.warn(`Unknown VUE_APP_CLIENT "${CLIENT}", using the default feature flags`);
}

const flags = { ...PROFILES.default, ...(PROFILES[CLIENT] || {}) };

export const ENABLE_ASSESSMENT = flags.ENABLE_ASSESSMENT;
export const ENABLE_CUSTOM_PROGRESS = flags.ENABLE_CUSTOM_PROGRESS;
export const ENABLE_ACADEMY_147_PDF = flags.ENABLE_ACADEMY_147_PDF;
export const ENABLE_SKILL_P1_P2 = flags.ENABLE_SKILL_P1_P2;
export const SHOW_AC_REG = flags.SHOW_AC_REG;
export const EXTRA_TASK_SCOPES = flags.EXTRA_TASK_SCOPES;
export const ENABLE_PASS_RATE = flags.ENABLE_PASS_RATE;
export const ENFORCE_EVALUATION_MIN_TASKS = flags.ENFORCE_EVALUATION_MIN_TASKS;
export const ENABLE_PRODUCT_DOCUMENTS = flags.ENABLE_PRODUCT_DOCUMENTS;
export const ENABLE_PRODUCT_REVISIONS = flags.ENABLE_PRODUCT_REVISIONS;
export const ENABLE_LOGBOOK_RULES = flags.ENABLE_LOGBOOK_RULES;
export const ENABLE_EMPTY_LOGBOOK = flags.ENABLE_EMPTY_LOGBOOK;
export const SHOW_UPCOMING_SESSIONS = flags.SHOW_UPCOMING_SESSIONS;
export const ARCHIVE_FULLY_ASSESSED_SESSIONS = flags.ARCHIVE_FULLY_ASSESSED_SESSIONS;
export const ENABLE_USER_BIRTH_AND_CATEGORY = flags.ENABLE_USER_BIRTH_AND_CATEGORY;
export const USER_ROLE_OPTIONS = flags.USER_ROLE_OPTIONS;
export const LOGIN_LOGO = flags.LOGIN_LOGO;
export const HELP_DOCS_PATH = flags.HELP_DOCS_PATH;
